import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { type SweetAlertOptions } from "sweetalert2";

import { SWAL_SETTINGS } from "../../../../../../../utils/Constants.js";
import { selectGuestCountFor } from "../../../../../../../redux/slices/guestCount.ts";
import { useAllTogetherDiscounts } from "./useAllTogetherDiscounts.ts";
import { useOrderData, type CombinedOrderItem } from "./useOrderData.ts";
import { useOrderMutations } from "./useOrderMutations.ts";
import { useScrollIndicator } from "./useScrollIndicator.ts";
import { useSplitPayment } from "./useSplitPayment.ts";
import type {
  RootState,
  SidebarTab,
  UseCashierHomeLayoutProps,
  UseCashierHomeLayoutReturn,
} from "./types.ts";
import { fireToast as sharedFireToast } from "../utils/toastHelpers.ts";
import { ORDER_TYPE } from "../utils/constants.ts";

const trash = "../../../../../../assets/restaurant/images/trash.svg";
const TOAST_SETTINGS = SWAL_SETTINGS as Partial<SweetAlertOptions>;

export function useCashierHomeLayout({
  tableId,
  orderType,
}: UseCashierHomeLayoutProps): UseCashierHomeLayoutReturn {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const restSettingDetails = useSelector((state: RootState) => state?.restSettings?.data);
  const guestCount = useSelector(selectGuestCountFor(tableId));

  const [activeSidebarTab, setActiveSidebarTab] = useState<SidebarTab>("items");
  const orderItemsPanelRef = useRef<HTMLDivElement>(null);

  const {
    tableDetail,
    orderItems,
    orderPriceDetail,
    availableItems,
    transactionData,
    isOrderValid,
    reloadOrderData,
  } = useOrderData({ tableId, orderType, taxRoundType: restSettingDetails?.tax_when_register_product as string | undefined });

  const discounts = useAllTogetherDiscounts({
    tableDetail,
    SWAL_SETTINGS: TOAST_SETTINGS,
    trash,
    onOrderChanged: reloadOrderData,
  });

  const splitPayment = useSplitPayment({
    tableId,
    orderType,
    tableDetail,
    availableItems,
    orderPriceDetail,
    giftCardApplied: discounts.giftCardApplied,
    couponCodeApplied: discounts.couponCodeApplied,
    clearAllTogetherDiscounts: discounts.clearAllTogetherDiscounts,
    restSettingDetails,
    SWAL_SETTINGS: TOAST_SETTINGS,
    isOrderValid,
  });

  const fireToast = useCallback(
    (icon: SweetAlertOptions["icon"], message?: string): void => {
      sharedFireToast(t, TOAST_SETTINGS, icon, message);
    },
    [t],
  );

  const mutations = useOrderMutations({
    tableId,
    orderType,
    transactionData,
    orderItems,
    hasSplitPayment: splitPayment.hasSplitPayment,
    guestCount,
    reloadOrderData,
    fireToast,
  });

  const {
    showOrderItemsScrollIndicator,
    updateOrderItemsScrollIndicator,
    handleOrderItemsScrollIndicatorClick,
  } = useScrollIndicator({
    activeSidebarTab,
    itemCount: orderItems.length,
    panelRef: orderItemsPanelRef,
  });

  const totalOrderQuantity = useMemo(
    () => orderItems.reduce(
      (sum: number, item: CombinedOrderItem) => sum + Number(item?.quantity || 0),
      0,
    ),
    [orderItems],
  );

  useEffect(() => {
    document.addEventListener("item_added", reloadOrderData);
    return () => document.removeEventListener("item_added", reloadOrderData);
  }, [reloadOrderData]);

  useEffect(() => {
    if (orderType === ORDER_TYPE.SPLIT_PAY) {
      setActiveSidebarTab("payment");
    }
  }, [orderType]);

  const handleSubmit = useCallback(
    (event?: React.SyntheticEvent): void => {
      event?.preventDefault();
      if (splitPayment.isSplitCheckout) return;

      if (orderType === ORDER_TYPE.EDIT_SALES) {
        navigate(`/cashier/payment-via-cash/${transactionData.order_id}?type=${orderType}`);
        return;
      }

      navigate(`/cashier/payment-via-cash/${tableId}`);
    },
    [navigate, orderType, splitPayment.isSplitCheckout, tableId, transactionData.order_id],
  );

  const ui = useMemo(
    () => ({
      activeSidebarTab,
      setActiveSidebarTab,
      orderItemsPanelRef,
      showOrderItemsScrollIndicator,
      updateOrderItemsScrollIndicator,
      handleOrderItemsScrollIndicatorClick,
    }),
    [
      activeSidebarTab,
      showOrderItemsScrollIndicator,
      updateOrderItemsScrollIndicator,
      handleOrderItemsScrollIndicatorClick,
    ],
  );

  const order = useMemo(
    () => ({
      tableId,
      orderType,
      tableDetail,
      orderItems,
      orderPriceDetail,
      totalOrderQuantity,
      isOrderValid,
      restSettingDetails,
    }),
    [
      tableId,
      orderType,
      tableDetail,
      orderItems,
      orderPriceDetail,
      totalOrderQuantity,
      isOrderValid,
      restSettingDetails,
    ],
  );

  return {
    ui,
    order,
    checkout: { handleSubmit },
    mutations,
    discounts,
    splitPayment,
  };
}
